import React, { useRef, useState, useCallback } from "react";
import {
    Upload,
    Paperclip,
    X,
    Loader2,
    FileText,
    Download,
    AlertCircle,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

/**
 * Iter60.p67 — Universal multi-file attachment field for Leave Requests.
 *
 * Iter89 — Modernised with drag-and-drop zone, inline file metadata
 * (filename + size + mime), per-file upload status, download links on
 * saved rows. Fixes Bug A (500 on upload — root cause was in
 * leave_attachments.py's GridFS bucket, patched there) and Bug B (silent
 * attach-on-create drop — the create-then-upload sequence now surfaces
 * individual file errors via the live-upload path).
 *
 * Two modes:
 *   1. Staged mode — the leave request does NOT yet exist. Files are
 *      kept in-memory (`value` is a File[] managed by the parent).
 *      The parent uploads them AFTER creating the request.
 *   2. Live mode — pass `requestId` and `liveAttachments`. Each pick
 *      uploads directly to
 *      `POST /api/leave/requests/{requestId}/attachments`.
 *      Deletes call the DELETE endpoint.
 *
 * Props:
 *   - value           File[] — staged files (mode 1 only)
 *   - onChange        (File[]) => void — for mode 1
 *   - requestId       string — mode 2 trigger
 *   - liveAttachments []attachment — server-side metadata (mode 2)
 *   - onLiveChange    (attachments[]) => void — invoked after add/delete
 *   - label           top-of-field label
 *   - required        bool — flavours the label + red asterisk
 *   - testid          data-testid prefix
 *   - canDelete       (attachment) => bool — mode 2 permission hook
 *   - maxFiles        cap (default 5)
 *   - maxMB           per-file MB cap (default 25)
 *   - disabled        disables the picker
 */
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.mp4,.mov";
const ACCEPT_LABEL = "PDF, image, DOCX, XLSX, MP4";

function humanSize(bytes) {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fileExtMime(name = "", mime = "") {
    const dot = name.lastIndexOf(".");
    const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
    return ext ? ext.toUpperCase() : (mime.split("/").pop() || "").toUpperCase();
}

export default function AttachmentsField({
    value = [],
    onChange,
    requestId = null,
    liveAttachments = [],
    onLiveChange,
    label = "Attachments (optional)",
    required = false,
    testid = "leave-attachments",
    canDelete,
    maxFiles = 5,
    maxMB = 25,
    disabled = false,
}) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    // Per-file transient status map keyed by filename+size+index while
    // the live upload is in-flight. Clears automatically on success.
    const [uploadStatus, setUploadStatus] = useState({}); // { key: "uploading"|"done"|"error", error?: string }

    const isLive = !!requestId;
    const list = isLive ? liveAttachments || [] : value || [];
    const currentCount = list.length;
    const slotsLeft = Math.max(0, maxFiles - currentCount);

    const pick = () => {
        if (disabled || busy) return;
        inputRef.current?.click();
    };

    const handleFiles = useCallback(
        async (incoming) => {
            if (!incoming.length) return;

            const maxBytes = maxMB * 1024 * 1024;
            const oversize = incoming.find((f) => f.size > maxBytes);
            if (oversize) {
                toast.error(`"${oversize.name}" exceeds ${maxMB} MB`);
                return;
            }
            if (currentCount + incoming.length > maxFiles) {
                toast.error(
                    `Maximum ${maxFiles} files. You already have ${currentCount}.`,
                );
                return;
            }

            if (!isLive) {
                onChange && onChange([...(value || []), ...incoming]);
                return;
            }

            // Live mode — upload each file sequentially and surface
            // per-file success / failure (Iter89 Bug A/B UX fix).
            setBusy(true);
            const next = [...(liveAttachments || [])];
            const statusDraft = { ...uploadStatus };
            let okCount = 0;
            let failCount = 0;
            for (const f of incoming) {
                const key = `${f.name}:${f.size}:${f.lastModified}`;
                statusDraft[key] = { state: "uploading" };
                setUploadStatus({ ...statusDraft });
                try {
                    const fd = new FormData();
                    fd.append("file", f);
                    const { data } = await api.post(
                        `/leave/requests/${requestId}/attachments`,
                        fd,
                        { headers: { "Content-Type": "multipart/form-data" } },
                    );
                    next.push(data);
                    statusDraft[key] = { state: "done" };
                    okCount += 1;
                } catch (err) {
                    statusDraft[key] = {
                        state: "error",
                        error: formatApiError(err),
                    };
                    failCount += 1;
                }
                setUploadStatus({ ...statusDraft });
            }
            onLiveChange && onLiveChange(next);
            setBusy(false);
            if (okCount > 0 && failCount === 0) {
                toast.success(
                    okCount > 1
                        ? `${okCount} files uploaded`
                        : "File uploaded",
                );
                // Clear done markers on success.
                setTimeout(() => setUploadStatus({}), 1500);
            } else if (okCount > 0 && failCount > 0) {
                toast.error(
                    `${okCount} uploaded, ${failCount} failed. See list below.`,
                );
            } else {
                toast.error(
                    failCount > 1
                        ? `${failCount} uploads failed`
                        : "Upload failed",
                );
            }
        },
        [
            value,
            liveAttachments,
            isLive,
            requestId,
            currentCount,
            maxFiles,
            maxMB,
            onChange,
            onLiveChange,
            uploadStatus,
        ],
    );

    const onPicked = async (e) => {
        const files = Array.from(e.target.files || []);
        if (inputRef.current) inputRef.current.value = "";
        await handleFiles(files);
    };

    const onDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (disabled || busy) return;
        const files = Array.from(e.dataTransfer?.files || []);
        await handleFiles(files);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || busy) return;
        if (!dragOver) setDragOver(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };

    const removeStaged = (idx) => {
        if (disabled) return;
        const next = value.filter((_, i) => i !== idx);
        onChange && onChange(next);
    };

    const removeLive = async (att) => {
        if (disabled) return;
        if (canDelete && !canDelete(att)) {
            toast.error("You cannot remove this attachment.");
            return;
        }
        setDeletingId(att.id);
        try {
            await api.delete(
                `/leave/requests/${requestId}/attachments/${att.id}`,
            );
            const next = (liveAttachments || []).filter(
                (a) => a.id !== att.id,
            );
            onLiveChange && onLiveChange(next);
            toast.success("Attachment removed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeletingId(null);
        }
    };

    const dropzoneBorder = dragOver
        ? "border-[color:var(--asl-navy)]"
        : "border-[color:var(--asl-border)]";
    const dropzoneBg = dragOver
        ? "bg-[color:var(--asl-navy)]/5"
        : "bg-gray-50 hover:bg-gray-100";

    const stagedStatusMarkers = Object.values(uploadStatus).filter(
        (s) => s.state === "error",
    );

    return (
        <div className="space-y-1.5" data-testid={testid}>
            <div className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)] flex items-center gap-1">
                <span data-testid={`${testid}-label`}>{label}</span>
                {required && (
                    <span
                        className="asl-crimson-text"
                        aria-label="required"
                        data-testid={`${testid}-required-mark`}
                    >
                        *
                    </span>
                )}
            </div>

            {/* File list */}
            {list.length > 0 && (
                <ul
                    className="space-y-1"
                    data-testid={`${testid}-list`}
                >
                    {list.map((item, idx) => {
                        const filename = isLive ? item.filename : item.name;
                        const size = isLive ? item.size_bytes : item.size;
                        const mime = isLive ? item.mime_type : item.type;
                        const key = isLive ? item.id : `${filename}-${idx}`;
                        const canRm = isLive
                            ? (canDelete ? canDelete(item) : true)
                            : true;
                        const extLabel = fileExtMime(filename, mime);
                        return (
                            <li
                                key={key}
                                className="flex items-center gap-2 px-3 py-2 rounded border bg-gray-50 text-xs"
                                style={{ borderColor: "var(--asl-border)" }}
                                data-testid={`${testid}-item-${idx}`}
                            >
                                <FileText className="h-4 w-4 asl-navy-text shrink-0" />
                                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                    {isLive ? (
                                        <a
                                            href={`${api.defaults.baseURL}/leave/requests/${requestId}/attachments/${item.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="asl-navy-text underline truncate font-medium"
                                            data-testid={`${testid}-link-${idx}`}
                                            title={filename}
                                        >
                                            {filename}
                                        </a>
                                    ) : (
                                        <span
                                            className="truncate font-medium"
                                            data-testid={`${testid}-name-${idx}`}
                                            title={filename}
                                        >
                                            {filename}
                                        </span>
                                    )}
                                    <span
                                        className="text-[10px] text-[color:var(--asl-muted)] mono"
                                        data-testid={`${testid}-meta-${idx}`}
                                    >
                                        {extLabel ? `${extLabel} · ` : ""}
                                        {humanSize(size)}
                                    </span>
                                </div>
                                {isLive && (
                                    <a
                                        href={`${api.defaults.baseURL}/leave/requests/${requestId}/attachments/${item.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[color:var(--asl-muted)] hover:asl-navy-text"
                                        title="Download"
                                        data-testid={`${testid}-download-${idx}`}
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                    </a>
                                )}
                                {canRm && !disabled && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            isLive
                                                ? removeLive(item)
                                                : removeStaged(idx)
                                        }
                                        className="text-[color:var(--asl-muted)] hover:asl-crimson-text"
                                        data-testid={`${testid}-remove-${idx}`}
                                        disabled={deletingId === item.id}
                                        title="Remove"
                                    >
                                        {deletingId === item.id ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <X className="h-3.5 w-3.5" />
                                        )}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* Per-file error summaries (live mode only) */}
            {stagedStatusMarkers.length > 0 && (
                <ul
                    className="space-y-0.5"
                    data-testid={`${testid}-errors`}
                >
                    {Object.entries(uploadStatus)
                        .filter(([, s]) => s.state === "error")
                        .map(([k, s]) => (
                            <li
                                key={k}
                                className="text-[11px] asl-crimson-text flex items-start gap-1"
                                data-testid={`${testid}-error-row`}
                            >
                                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                <span className="truncate">
                                    {k.split(":")[0]} — {s.error}
                                </span>
                            </li>
                        ))}
                </ul>
            )}

            {/* Drop zone — hidden when the slot cap is reached */}
            {slotsLeft > 0 && (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={pick}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            pick();
                        }
                    }}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragEnter={onDragOver}
                    onDragLeave={onDragLeave}
                    className={`relative w-full px-4 py-5 rounded border-2 border-dashed cursor-pointer transition-colors flex flex-col items-center justify-center gap-1 ${dropzoneBorder} ${dropzoneBg} ${
                        disabled || busy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                    data-testid={`${testid}-dropzone`}
                    data-dragover={dragOver ? "true" : "false"}
                    aria-disabled={disabled || busy}
                >
                    {busy ? (
                        <>
                            <Loader2 className="h-5 w-5 animate-spin asl-navy-text" />
                            <span
                                className="text-xs asl-navy-text"
                                data-testid={`${testid}-uploading`}
                            >
                                Uploading…
                            </span>
                        </>
                    ) : (
                        <>
                            <Upload className="h-5 w-5 asl-navy-text" />
                            <span className="text-xs asl-navy-text font-medium">
                                {dragOver
                                    ? "Drop to upload"
                                    : "Drag files here or click to browse"}
                            </span>
                            <span className="text-[10px] text-[color:var(--asl-muted)]">
                                {ACCEPT_LABEL} · up to {maxMB} MB each ·{" "}
                                {slotsLeft} slot
                                {slotsLeft === 1 ? "" : "s"} remaining
                            </span>
                        </>
                    )}
                    {/* Successful live-upload acknowledgement (fades) */}
                    {Object.values(uploadStatus).some(
                        (s) => s.state === "done",
                    ) &&
                        !busy && (
                            <span
                                className="absolute top-1.5 right-2 flex items-center gap-1 text-[10px] text-emerald-700"
                                data-testid={`${testid}-upload-ok`}
                            >
                                <CheckCircle2 className="h-3 w-3" /> Added
                            </span>
                        )}
                </div>
            )}

            {slotsLeft === 0 && (
                <div
                    className="text-[11px] text-[color:var(--asl-muted)] flex items-center gap-1"
                    data-testid={`${testid}-full`}
                >
                    <Paperclip className="h-3 w-3" />
                    Maximum of {maxFiles} files attached. Remove one to add
                    another.
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                hidden
                multiple
                onChange={onPicked}
                data-testid={`${testid}-input`}
            />
        </div>
    );
}
